/* eslint-disable no-underscore-dangle */
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { AuthenticationService, DocumentsService } from '@app/core/services';
import { IAGServiceLineTableConfig } from '@app/products/pac/models/ag-service-line-table-config.model';
import { TableColumnConfig } from '@app/shared/models';
import { LibPipeModule } from '@mynexus/mynexus-ui-lib';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { Table, TableModule } from 'primeng/table';

@Component({
    selector: 'mnc-service-lines-selection',
    templateUrl: './service-lines-selection.component.html',
    imports: [
        CommonModule,
        LibPipeModule,
        MultiSelectModule,
        TableModule,
        InputTextModule,
    ]
})
export class ServiceLinesSelectionComponent {
  @ViewChild('dt', { static: true }) dataTable!: Table;

  @Input() set lines(value: object[]) {
        if(value) {
          this._lines = value;
          this._lines = this._lines?.map((item: any) => {
            const isNecessaryStatus = this.statusListWithVisits.includes(item.serviceStatus as string);
            return {
              ...item,
              daysApproved: isNecessaryStatus ? item.daysApproved : null,
              daysDenied: isNecessaryStatus ? item.daysDenied : null,
            };
          });
          this.defineTable();
        }
  }
  @Output() serviceLinesSelected = new EventEmitter<string[]>();

  tableData: IAGServiceLineTableConfig[];
  columnConfig: TableColumnConfig[] = [];
  selection: object;
  private _lines!: object[];

  private readonly documentsService = inject(DocumentsService);
  readonly statusListWithVisits = ['Approved', 'Denied', 'Partially Favorable'];
  constructor(private readonly authenticationService: AuthenticationService,
   ) {

  }

  private defineTable() {
    this.tableData = this.initTableData();
    this.columnConfig = this.initTableConfig();
  }


  selectionChange(data: IAGServiceLineTableConfig[]) {
    this.serviceLinesSelected.emit(data.map((x) => x.serviceLineId));
  }

  clearFilters(): void {
    this.dataTable.clear();
     this.columnConfig = this.initTableConfig();
  }

  private initTableData(): IAGServiceLineTableConfig[] {
    return this._lines?.map((x) => ({
      serviceLineId: x['serviceLineId'],
      serviceLineNumber: x['serviceNumber'],
      requestReceivedDate: x['requestReceivedDate'],
      serviceRequestType: x['serviceRequestType'],
      urgency: x['urgency'],
      reimbursementType: x['reimbursementType'],
      serviceStatus: x['serviceStatus'],
      effectiveFrom: x['effectiveStartDate'],
      effectiveThrough: x['effectiveEndDate'],
      approvedDays: x['daysApproved'],
      deniedDays: x['daysDenied'],
      statusReason: x['serviceStatusReason'],
      queue: this.authenticationService.isExternalUser ? undefined : x['queue'],
    }))?.sort((a, b) => { return b.serviceLineNumber - a.serviceLineNumber });
  }

  private initTableConfig(): TableColumnConfig[] {
    const serviceLineNumberList = this.documentsService
    .getUniqueFilterOptions(this.tableData.map((x) => x.serviceLineNumber));

    const serviceRequestTypeList = this.documentsService
      .getUniqueFilterOptions(this.tableData.map((x) => x.serviceRequestType));

    const serviceStatusList = this.documentsService
      .getUniqueFilterOptions(this.tableData.map((x) => x.serviceStatus));

    const result = [
      {
        header: 'Service Line Number',
        field: 'serviceLineNumber',
        styles: { minWidth: '190px' },
        isSortable: true,
        filterTemplate: 'multiSelectFilterTemplate',
        filterList: serviceLineNumberList,
      },
      {
        header: 'Date/Time Request Received',
        field: 'requestReceivedDate',
        styles: { minWidth: '250px' },
        isSortable: true,
      },
      {
        header: 'Service Request Type',
        field: 'serviceRequestType',
        styles: { minWidth: '240px' },
        isSortable: true,
        filterTemplate: 'multiSelectFilterTemplate',
        filterList: serviceRequestTypeList,
      },
      {
        header: 'Urgency',
        field: 'urgency',
        styles: { minWidth: '160px' },
        isSortable: true,
      },
      {
        header: 'Reimbursement Type',
        field: 'reimbursementType',
        styles: { minWidth: '210px' },
        isSortable: true,
      },
      {
        header: 'Service Status',
        field: 'serviceStatus',
        styles: { minWidth: '190px' },
        isSortable: false,
        filterTemplate: 'multiSelectFilterTemplate',
        filterList: serviceStatusList,
      },
      {
        header: 'Effective From',
        field: 'effectiveFrom',
        styles: { minWidth: '180px' },
        isSortable: true,
      },
      {
        header: 'Effective Through',
        field: 'effectiveThrough',
        styles: { minWidth: '180px' },
        isSortable: true,
      },
      {
        header: 'Approved Days',
        field: 'approvedDays',
        styles: { minWidth: '170px' },
        isSortable: true,
      },
      {
        header: 'Denied Days',
        field: 'deniedDays',
        styles: { minWidth: '170px' },
        isSortable: true,
      },
      {
        header: 'Status Reason',
        field: 'statusReason',
        styles: { minWidth: '250px' },
        isSortable: true,
      },
    ];

    // external users cannot see queue column
    if (!this.authenticationService.isExternalUser) {
      result.push({
        header: 'Queue',
        field: 'queue',
        styles: { minWidth: '180px' },
        isSortable: true,
      });
    }

    return result;
  }
}
